import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import * as Types from 'pages/Pages/typings';
import makeDeleteEntityType from 'fetches/makeDeleteEntityType';
import { useDispatch } from 'react-redux';
import { isLoading, isNormal, useStatus } from 'resift';
import showSnackbar from 'helpers/showSnackbar';
import pluralize from 'pluralize';
import usePrevious from '@sift/resift/usePrevious';
import { useHistory } from 'react-router';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import LoadingButton from 'atoms/LoadingButton';

import makeStyles from '@material-ui/core/styles/makeStyles';

interface Props {
  entityType: Types.EntityType;
}

function DeletePages({ entityType }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const deleteEntityType = makeDeleteEntityType(entityType.id);
  const deleteEntityTypeStatus = useStatus(deleteEntityType);
  const prevDeleteEntityTypeStatus = usePrevious(deleteEntityTypeStatus);
  const isDeleting = isLoading(deleteEntityTypeStatus);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInput, setDialogInput] = useState('');
  const buttonClass = classnames({
    [classes.red]: dialogInput === pluralize(entityType.name, 1),
    [classes.disabled]: dialogInput !== pluralize(entityType.name, 1),
  });

  const handleDialogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDialogInput(e.target.value);
  };

  const toggleDialog = async (shouldDeletePage: boolean) => {
    if (shouldDeletePage) {
      await dispatch(deleteEntityType());
    }
    setDialogInput('');
    setDialogOpen(!dialogOpen);
  };

  showSnackbar({
    status: deleteEntityTypeStatus,
    message: `${entityType.name} deleted.`,
    errorMessage: 'An error occurred while deleting your page.',
  });

  useEffect(() => {
    if (isLoading(prevDeleteEntityTypeStatus) && isNormal(deleteEntityTypeStatus)) {
      history.push('/admin/pages');
    }
  }, [deleteEntityTypeStatus, prevDeleteEntityTypeStatus]);

  return (
    <div className={classes.root}>
      <Card>
        <CardContent>
          <Typography variant="h6">Delete Pages</Typography>
          <Typography className={classes.marginBottom1} variant="subtitle2">
            Deleting pages will permanently remove all pages and their profile data.
          </Typography>
          <Button
            classes={{ root: classes.openDialogButton }}
            data-testid="delete pages"
            disableElevation={true}
            onClick={() => {
              toggleDialog(false);
            }}
            variant="contained"
          >
            Delete Pages
          </Button>
        </CardContent>
      </Card>
      <Dialog
        aria-labelledby="delete-confirmation"
        open={dialogOpen}
        onClose={() => toggleDialog(false)}
      >
        <DialogTitle id="delete-confirmation">
          Are you sure you want to delete your {pluralize(entityType.name, 1)} pages?
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.marginBottom3}>
            Deleting pages will permanently remove all pages and their profile data. You cannot undo
            this action. To delete pages, type out "{pluralize(entityType.name, 1)}" in the input
            below to enable the delete button. If you would like to transition your attribute back
            to a simpler form without pages, please contact us at{' '}
            <a href="mailto:support@justsift.com">support@justsift.com</a>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={`Type ${pluralize(entityType.name, 1)}`}
            value={dialogInput}
            inputProps={{ 'aria-label': 'page name input' }}
            onChange={handleDialogChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isDeleting}
            onClick={() => {
              toggleDialog(false);
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            className={buttonClass}
            data-testid="submit-deletion"
            onClick={() => {
              toggleDialog(true);
            }}
            disabled={dialogInput !== pluralize(entityType.name, 1)}
            label="Delete"
            variant="contained"
            loading={isDeleting}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  marginBottom1: {
    marginBottom: theme.spacing(),
  },
  openDialogButton: {
    color: theme.palette.common.white,
    background: theme.palette.error.main,
    marginTop: theme.spacing(),
  },
  red: {
    color: theme.palette.common.white,
    background: theme.palette.error.main,
  },
  disabled: {
    cursor: 'not-allowed',
  },
  marginBottom3: {
    marginBottom: theme.spacing(3),
  },
}));

export default DeletePages;
